import React, { useState, useEffect } from 'react';
import { Check, ShoppingCart, X, Package, Sparkles } from 'lucide-react';

interface CartNotificationProps {
  isVisible: boolean;
  onClose: () => void;
  productName: string;
  isPersonalized?: boolean;
  ingredientCount?: number;
  totalPrice?: number;
}

export default function CartNotification({ 
  isVisible, 
  onClose, 
  productName, 
  isPersonalized = false,
  ingredientCount = 0,
  totalPrice 
}: CartNotificationProps) {
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Auto-close after 4 seconds
      const timer = setTimeout(() => {
        onClose();
      }, 4000);
      return () => clearTimeout(timer);
    } else {
      // Delay unmounting to allow exit animation
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!shouldRender) return null;

  return (
    <>
      <style jsx>{`
        .notification-enter {
          transform: translateX(100%);
          opacity: 0;
        }
        
        .notification-enter-active {
          transform: translateX(0);
          opacity: 1;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .notification-exit {
          transform: translateX(0);
          opacity: 1;
        }
        
        .notification-exit-active {
          transform: translateX(100%);
          opacity: 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .notification-glow {
          box-shadow: 0 10px 40px rgba(34, 197, 94, 0.3);
        }
        
        @keyframes checkmark {
          0% { transform: scale(0) rotate(45deg); }
          50% { transform: scale(1.2) rotate(45deg); }
          100% { transform: scale(1) rotate(45deg); }
        }
        
        .checkmark-animation {
          animation: checkmark 0.6s ease-out;
        }
        
        @keyframes slideInBounce {
          0% { 
            transform: translateX(100%);
            opacity: 0;
          }
          60% { 
            transform: translateX(-10px);
            opacity: 1;
          }
          100% { 
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        .slide-in-bounce {
          animation: slideInBounce 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
      `}</style>

      <div className={`fixed top-4 right-4 z-[60] ${isVisible ? 'slide-in-bounce' : 'notification-exit-active'}`}>
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl shadow-2xl notification-glow border border-green-400/30 p-4 sm:p-6 max-w-sm">
          <div className="flex items-start space-x-3 sm:space-x-4">
            {/* Success Icon */}
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
              <Check className="h-4 w-4 sm:h-5 sm:w-5 text-white checkmark-animation" />
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-white text-sm sm:text-base mb-1">
                Added to Cart!
              </h3>
              <p className="text-green-100 text-xs sm:text-sm mb-2">
                {productName}
                {isPersonalized && ingredientCount > 0 && (
                  <span className="block text-xs text-green-200 mt-1">
                    <Sparkles className="h-3 w-3 inline mr-1" />
                    Personalized with {ingredientCount} ingredient{ingredientCount !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
              
              {totalPrice && (
                <div className="flex items-center space-x-2 text-xs sm:text-sm">
                  <Package className="h-3 w-3 sm:h-4 sm:w-4 text-green-200" />
                  <span className="text-green-100">Total: S${totalPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              className="text-green-200 hover:text-white transition-colors p-1"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-3 sm:mt-4 w-full bg-white/20 rounded-full h-1">
            <div 
              className="bg-white h-1 rounded-full transition-all duration-[4000ms] ease-linear"
              style={{ 
                width: isVisible ? '0%' : '100%',
                transition: isVisible ? 'width 4s linear' : 'none'
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}